import { useCallback, useMemo, useState } from "react";
import { Box, Flex, HStack, Stack, Tooltip, useDisclosure } from "@chakra-ui/react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { format, formatDistanceToNow } from "date-fns";
import CDS from "design-system";

import GenericTable from "components/Table/GenericTable";
import { InternalLink } from "components/Link";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import type { SortOption } from "components/ControlBar/types";
import useSideDrawer from "hooks/useSideDrawer";
import { FiDownload } from "react-icons/fi";

import { customFormKeys } from "../hooks";
import { fetchCustomFormResponses } from "../api";
import type { CustomFormResponse } from "../types";
import ResponseDetailDrawer from "./ResponseDetailDrawer";
import ExportCsvDialog from "./ExportCsvDialog";

const columnHelper = createColumnHelper<CustomFormResponse>();

const SORT_OPTIONS: SortOption[] = [
  { label: "Newest first", value: "submitted_at_desc" },
  { label: "Oldest first", value: "submitted_at_asc" },
  { label: "Submitter (A-Z)", value: "submitter_asc" },
];
const DEFAULT_SORT = "submitted_at_desc";

// Sort values are `<field>_<asc|desc>`; the field portion can contain underscores.
function splitSort(value: string): { sortBy: string; sortDirection: "asc" | "desc" } {
  const idx = value.lastIndexOf("_");
  if (idx === -1) return { sortBy: value, sortDirection: "asc" };
  const direction = value.slice(idx + 1);
  return {
    sortBy: value.slice(0, idx),
    sortDirection: direction === "desc" ? "desc" : "asc",
  };
}

interface Props {
  formId: number | string;
}

export default function ResponsesTable({ formId }: Props) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const { isOpen: isExportOpen, onOpen: openExport, onClose: closeExport } = useDisclosure();

  const drawer = useSideDrawer<CustomFormResponse, string>({
    getItemId: (r) => (r ? String(r.id) : undefined),
  });

  const additionalQueries = useMemo(() => {
    const { sortBy, sortDirection } = splitSort(sort);
    return {
      q: search || undefined,
      sort_by: sortBy,
      sort_direction: sortDirection,
    };
  }, [search, sort]);

  const fetchData = useCallback(
    (query: string, signal?: AbortSignal) => fetchCustomFormResponses(formId, query, signal),
    [formId],
  );

  const columns = useMemo<ColumnDef<CustomFormResponse, any>[]>(
    () => [
      columnHelper.accessor((row) => row, {
        id: "submitter",
        header: "Submitted by",
        enableSorting: false,
        cell: (row) => {
          const r = row.getValue();
          const name = r.submittedBy?.fullName ?? "—";
          const email = r.submittedBy?.workEmail;
          return (
            <Stack spacing={0}>
              <CDS.Text12 fontWeight="bold">{name}</CDS.Text12>
              {email && <CDS.Text12 color="fg.subtle">{email}</CDS.Text12>}
            </Stack>
          );
        },
      }),
      columnHelper.accessor((row) => row.submittedAt, {
        id: "submittedAt",
        header: "Submitted",
        enableSorting: false,
        cell: (row) => {
          const value = row.getValue();
          if (!value) return <CDS.Text12>—</CDS.Text12>;
          const parsed = new Date(value);
          return (
            <Tooltip label={format(parsed, "MMM d, yyyy 'at' h:mm a")} placement="top" hasArrow>
              <Box as="span" cursor="default">
                <CDS.Text12>{formatDistanceToNow(parsed, { addSuffix: true })}</CDS.Text12>
              </Box>
            </Tooltip>
          );
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "view",
        header: "",
        enableSorting: false,
        cell: (row) => {
          const r = row.getValue();
          return (
            <Box textAlign="right" w="100%">
              <InternalLink
                to="#"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  drawer.expandClickHandlerByItem(r);
                }}
                color="blue.600"
              >
                <CDS.Text12 fontWeight="bold">View</CDS.Text12>
              </InternalLink>
            </Box>
          );
        },
      }),
    ],
    [drawer],
  );

  const hasActiveFilters = !!search;

  return (
    <Stack spacing={4} w="100%">
      <FilterSortSearchBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name or email..."
        currentSort={sort}
        defaultSort={DEFAULT_SORT}
        onSortChange={setSort}
        sortOptions={SORT_OPTIONS}
        activeFilters={[]}
        onReset={() => {
          setSearch("");
          setSort(DEFAULT_SORT);
        }}
        hasActiveFilters={hasActiveFilters}
      />

      <Flex justify="flex-end" align="center">
        <HStack>
          <CDS.Button
            intent={CDS.Button.intent.secondary}
            variant={CDS.Button.variant.outline}
            size="sm"
            leftIcon={{ customIcon: <FiDownload /> }}
            onClick={openExport}
          >
            Export CSV
          </CDS.Button>
        </HStack>
      </Flex>

      <GenericTable<CustomFormResponse>
        fetchData={fetchData}
        queryKey={(qs) => [...customFormKeys.responses(formId), qs]}
        columns={columns}
        additionalQueries={additionalQueries}
        pageSizeOptions={[25, 50, 100]}
        emptyStateComponent={<CDS.Text14 color="fg.subtle">No responses yet.</CDS.Text14>}
      />

      <ResponseDetailDrawer
        responseId={drawer.selectedId}
        isOpen={drawer.isOpen}
        onClose={drawer.onClose}
      />
      <ExportCsvDialog formId={formId} isOpen={isExportOpen} onClose={closeExport} />
    </Stack>
  );
}
